<template>
  <!-- Content-sized on purpose: NO height in modalStyle, so the dialog grows with the draft and stops
       at BaseModal's own max-height:90vh. That is what keeps the scrollbar away until the text really
       exceeds the viewport. closeOnBackdrop=false because the admin is editing here — a stray click
       outside must not yank the surface away, even though nothing could be lost. -->
  <BaseModal :open="open"
             maxWidth="1100px"
             :closeOnBackdrop="false"
             :position="['center', 'center']"
             :modalStyle="['display:flex', 'flex-direction:column']"
             @close="$emit('close')">
    <div class="v2026-draft" role="dialog" aria-modal="true" :aria-labelledby="titleId">

      <div class="v2026-draft__head">
        <h2 class="v2026-draft__title" :id="titleId">ตรวจทานข้อความก่อนส่ง</h2>
        <!-- decorative duplicate of the text itself — announcing it on every keystroke would be noise.
             The ceiling is shown here at ANY length on purpose: this is a surface the admin opens
             deliberately, so it costs the composer zero pixels. No ceiling exists = today's text. -->
        <span v-if="charCount" class="v2026-draft__count"
              :class="{ 'is-over': isOverLimit }" aria-hidden="true">
          <template v-if="textLimit !== null">{{ platformLabel }} {{ headCountText }}</template>
          <template v-else>{{ formatCount(charCount) }} ตัวอักษร</template>
        </span>
        <button type="button" class="v2026-draft__close" aria-label="ปิด"
                @click="$emit('close')">×</button>
      </div>

      <!-- the only scroll container in the overlay -->
      <div class="v2026-draft__body">
        <textarea ref="input"
                  class="v2026-draft__input"
                  placeholder="พิมพ์ข้อความที่นี่"
                  aria-label="ข้อความที่กำลังตรวจทาน"
                  :value="text"
                  @input="onInput"
                  @keydown.shift.enter.prevent="onSend"></textarea>
      </div>

      <div class="v2026-draft__foot" :class="{ 'has-limitwarn': isOverLimit }">
        <!-- over-limit only: its own full-width line so it never competes with the buttons -->
        <span v-if="isOverLimit" class="v2026-draft__limitwarn" role="status" aria-live="polite">
          <svg class="v2026-draft__limitglyph" viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
            <circle cx="12" cy="12" r="12" fill="currentColor"/>
            <path d="M12 6.25v7" fill="none" stroke="#ffffff" stroke-width="2" stroke-linecap="round"/>
            <circle cx="12" cy="17.25" r="1.35" fill="#ffffff"/>
          </svg>
          {{ warningLine }}
        </span>
        <!-- read-only: sending from here sends the queued files too, so the admin has to see them.
             Removal stays in the composer, where the badge and its × live. -->
        <span v-if="attachmentName" class="v2026-draft__attach">
          <span class="v2026-draft__attach-ico" aria-hidden="true" :style="maskStyle(attachIcon)"></span>
          แนบไฟล์: {{ attachmentName }}
        </span>
        <button type="button" class="v2026-draft__cancel" @click="$emit('close')">ปิด</button>
        <button type="button" class="v2026-draft__send"
                :disabled="isSending || !canSend || isOverLimit"
                @click="onSend">
          <span class="v2026-draft__send-ico" aria-hidden="true" :style="maskStyle(sendIcon)"></span>
          {{ isSending ? 'กำลังส่ง...' : 'ส่งข้อความ' }}
        </button>
      </div>

    </div>
  </BaseModal>
</template>

<script>
import BaseModal from '@/components/coversation/BaseModal.vue'
import { countMessageLength, counterText, warningText, formatCount } from '@/constants/messageLimits'

let uid = 0

export default {
  name: 'DraftReviewOverlayV2026',
  components: {
    BaseModal
  },
  props: {
    open: {
      type: Boolean,
      default: false
    },
    // the composer's own `message`. READ-ONLY here — there is no local copy, which is what makes
    // "closing can never discard text" structural rather than a promise.
    draft: {
      type: String,
      default: ''
    },
    isSending: {
      type: Boolean,
      default: false
    },
    attachmentName: {
      type: String,
      default: ''
    },
    sendIcon: {
      type: String,
      default: ''
    },
    attachIcon: {
      type: String,
      default: ''
    },
    // the three below are defaulted so the component's existing contract is unchanged for any
    // caller that does not pass them — nothing new renders and nothing new is disabled
    isOverLimit: {
      type: Boolean,
      default: false
    },
    platformLabel: {
      type: String,
      default: ''
    },
    textLimit: {
      type: Number,
      default: null
    }
  },
  data () {
    return {
      titleId: `v2026-draft-title-${++uid}`
    }
  },
  computed: {
    // `message` starts as null in the composer; the overlay never shows or emits null
    text () {
      return this.draft || ''
    },
    // same implementation the composer and the server enforce — one count, never two
    charCount () {
      return countMessageLength(this.text)
    },
    headCountText () {
      return counterText(this.charCount, this.textLimit)
    },
    warningLine () {
      return warningText(this.platformLabel)
    },
    canSend () {
      return !!this.text.trim() || !!this.attachmentName
    }
  },
  watch: {
    open (isOpen) {
      if (!isOpen) return
      this.$nextTick(() => {
        this.autoGrow()
        this.focusEnd()
      })
    },
    // covers every external writer — templates, applyComposerInsert, replyTranslateAi, speech
    draft () {
      this.$nextTick(this.autoGrow)
    }
  },
  mounted () {
    window.addEventListener('resize', this.autoGrow)
  },
  beforeDestroy () {
    window.removeEventListener('resize', this.autoGrow)
  },
  methods: {
    formatCount,
    maskStyle (url) {
      if (!url) return {}
      return {
        '-webkit-mask-image': `url(${url})`,
        'mask-image': `url(${url})`
      }
    },
    onInput (ev) {
      this.$emit('update:draft', ev.target.value)
      this.autoGrow()
    },
    // the textarea is never a scroll container — it is always exactly as tall as its content, so the
    // body owns the only scrollbar and the caret stays visible without a nested-scroll trap
    autoGrow () {
      const el = this.$refs.input
      if (!el) return
      el.style.height = 'auto'
      el.style.height = el.scrollHeight + 'px'
    },
    // caret at the end, never select-all — selecting invites overwriting the very draft being reviewed
    focusEnd () {
      const el = this.$refs.input
      if (!el) return
      el.focus()
      const end = el.value.length
      el.setSelectionRange(end, end)
    },
    // the parent decides what sending means; this overlay knows no endpoint
    onSend () {
      if (this.isSending || !this.canSend) return
      // belt-and-braces: the composer's own guard is the real defence, but the overlay must not be
      // able to emit a send it knows is impossible
      if (this.isOverLimit) return
      this.$emit('send')
    }
  }
}
</script>

<style scoped>
.v2026-draft {
  --v2026-draft-main:      #31C1D7;
  --v2026-draft-main-soft: #31C1D714;
  --v2026-draft-surface:   #FFFFFF;
  --v2026-draft-hover:     #E9ECF1;
  --v2026-draft-border:    #E4E7EC;
  --v2026-draft-hairline:  #EBEDF0;
  --v2026-draft-text:      #101828;
  --v2026-draft-secondary: #667085;
  --v2026-draft-muted:     #98A2B3;

  display: flex;
  flex-direction: column;
  /* shrinks against BaseModal's max-height:90vh instead of claiming a height of its own */
  flex: 1 1 auto;
  min-height: 0;
}

.v2026-draft__head {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 0 0 auto;
  padding: 14px 24px;
  background: var(--v2026-draft-surface);
  border-bottom: 1px solid var(--v2026-draft-hairline);
  border-top-left-radius: 24px;
  border-top-right-radius: 24px;
}
.v2026-draft__title {
  margin: 0;
  margin-right: auto;
  font-size: 15px;
  font-weight: 600;
  color: var(--v2026-draft-text);
}
.v2026-draft__count {
  flex: 0 0 auto;
  font-size: 13px;
  color: var(--v2026-draft-muted);
  white-space: nowrap;
}
.v2026-draft__close {
  flex: 0 0 auto;
  border: none;
  background: transparent;
  padding: 0;
  font-size: 20px;
  line-height: 1;
  color: #66696C;
  cursor: pointer;
}
.v2026-draft__close:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px var(--v2026-draft-main-soft);
  border-radius: 4px;
}

.v2026-draft__body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 16px 24px;
  background: var(--v2026-draft-surface);
}

.v2026-draft__input {
  display: block;
  width: 100%;
  padding: 0;
  border: 0;
  outline: none;
  background: transparent;
  resize: none;
  /* never a scroll container — see autoGrow() */
  overflow: hidden;
  font-family: inherit;
  font-size: 15px;
  line-height: 1.65;
  color: var(--v2026-draft-text);
  min-height: 120px;
}
.v2026-draft__input::placeholder {
  color: var(--v2026-draft-muted);
}

.v2026-draft__foot {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 0 0 auto;
  padding: 12px 24px;
  background: var(--v2026-draft-surface);
  border-top: 1px solid var(--v2026-draft-hairline);
  border-bottom-left-radius: 24px;
  border-bottom-right-radius: 24px;
}
/* wrapping is switched on ONLY while the warning row exists, so the footer of a normal draft keeps
   its exact current single-line flex behaviour */
.v2026-draft__foot.has-limitwarn {
  flex-wrap: wrap;
}
.v2026-draft__limitwarn {
  flex: 1 0 100%;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  /* not --v2026-red: 3.4:1 on white fails AA at 13px */
  color: #B42318;
  font-size: 13px;
  font-weight: 600;
  line-height: 1.3;
}
.v2026-draft__limitglyph {
  flex: 0 0 auto;
}
.v2026-draft__count.is-over {
  color: #B42318;
  font-weight: 600;
}
.v2026-draft__attach {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-right: auto;
  min-width: 0;
  font-size: 13px;
  color: var(--v2026-draft-secondary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.v2026-draft__attach-ico {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  display: inline-block;
  background-color: currentColor;
  -webkit-mask-repeat: no-repeat;
          mask-repeat: no-repeat;
  -webkit-mask-position: center;
          mask-position: center;
  -webkit-mask-size: contain;
          mask-size: contain;
}

/* the .v2026-keylad recipe, copied not imported — scoped styles do not cross components */
.v2026-draft__cancel {
  margin-left: auto;
  height: 36px;
  padding: 0 16px;
  border: 1px solid var(--v2026-draft-border);
  border-radius: 4px;
  background: var(--v2026-draft-surface);
  color: var(--v2026-draft-secondary);
  font-size: 13px;
  cursor: pointer;
  transition: background-color 150ms ease;
}
.v2026-draft__attach ~ .v2026-draft__cancel {
  margin-left: 0;
}
.v2026-draft__cancel:hover {
  background: var(--v2026-draft-hover);
  color: var(--v2026-draft-text);
}
.v2026-draft__cancel:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px var(--v2026-draft-main-soft);
}

/* the .v2026-send recipe, so the two send buttons read as one control in two places */
.v2026-draft__send {
  height: 40px;
  padding: 6px 14px;
  border: 0;
  border-radius: 8px;
  background: var(--v2026-draft-main);
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  transition: background-color 150ms ease, opacity 150ms ease;
}
.v2026-draft__send:hover:not(:disabled) {
  background: #2BAFC4;
}
.v2026-draft__send:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.v2026-draft__send:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px var(--v2026-draft-main-soft);
}
.v2026-draft__send-ico {
  width: 18px;
  height: 18px;
  flex-shrink: 0;
  display: inline-block;
  background-color: currentColor;
  -webkit-mask-repeat: no-repeat;
          mask-repeat: no-repeat;
  -webkit-mask-position: center;
          mask-position: center;
  -webkit-mask-size: contain;
          mask-size: contain;
}

@media (max-width: 767px) {
  .v2026-draft__head,
  .v2026-draft__foot {
    padding: 12px 16px;
  }
  .v2026-draft__title {
    font-size: 14px;
  }
  .v2026-draft__body {
    padding: 12px;
  }
}

/* buys ~40px of text back on a short window */
@media (max-height: 600px) {
  .v2026-draft__head {
    padding: 12px 24px;
  }
  .v2026-draft__foot {
    padding: 8px 24px;
  }
  .v2026-draft__body {
    padding: 12px 16px;
  }
}
</style>
